import { Cookie } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const LAST_UPDATED = "28 August 2026";

const CookiePolicy = () => {
  const { locale } = useI18n();
  const isIt = locale === "it";

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="page-container mx-auto py-12 space-y-8 max-w-4xl">
        <div className="glass-card p-8 space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <Cookie className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold">{isIt ? "Cookie Policy" : "Cookie Policy"}</h1>
          <p className="text-sm text-muted-foreground">
            {isIt ? "Ultimo aggiornamento" : "Last updated"}: {LAST_UPDATED}
          </p>
        </div>

        <div className="glass-card p-8 space-y-6 text-sm leading-relaxed text-muted-foreground">
          {isIt ? (
            <>
              <Section title="1. Cosa sono i cookie">
                I cookie sono piccoli file di testo memorizzati sul tuo dispositivo quando visiti il nostro sito
                web. Utilizziamo cookie e tecnologie simili (es. local storage) per far funzionare correttamente il
                sito e la dashboard.
              </Section>
              <Section title="2. Tipologie di cookie utilizzati">
                <strong className="text-foreground">Cookie essenziali:</strong> necessari per l'autenticazione
                (sessione OAuth Discord) e il funzionamento della dashboard. <br />
                <strong className="text-foreground">Cookie di preferenza:</strong> memorizzano lingua, tema e altre
                impostazioni dell'interfaccia. <br />
                <strong className="text-foreground">Cookie analitici:</strong> utilizzati, se attivi, per capire come
                viene utilizzato il sito e migliorarne le prestazioni.
              </Section>
              <Section title="3. Gestione dei cookie">
                Puoi gestire o disabilitare i cookie tramite le impostazioni del tuo browser. Disabilitare i cookie
                essenziali potrebbe impedire il corretto funzionamento della dashboard e del login.
              </Section>
              <Section title="4. Cookie di terze parti">
                Alcune funzionalità (es. autenticazione Discord, elaborazione pagamenti Premium) possono impostare
                cookie di terze parti soggetti alle rispettive informative sulla privacy.
              </Section>
              <Section title="5. Modifiche a questa policy">
                Potremmo aggiornare questa Cookie Policy periodicamente. Ti invitiamo a consultarla regolarmente.
              </Section>
              <Section title="6. Contatti">
                Per domande su questa Cookie Policy, contattaci tramite la pagina{" "}
                <a href="/support/contact" className="text-primary hover:underline">
                  Contattaci
                </a>
                .
              </Section>
            </>
          ) : (
            <>
              <Section title="1. What Are Cookies">
                Cookies are small text files stored on your device when you visit our website. We use cookies and
                similar technologies (e.g. local storage) to make the site and dashboard work correctly.
              </Section>
              <Section title="2. Types of Cookies We Use">
                <strong className="text-foreground">Essential cookies:</strong> required for authentication
                (Discord OAuth session) and dashboard functionality. <br />
                <strong className="text-foreground">Preference cookies:</strong> store language, theme, and other
                interface settings. <br />
                <strong className="text-foreground">Analytics cookies:</strong> used, when enabled, to understand
                site usage and improve performance.
              </Section>
              <Section title="3. Managing Cookies">
                You can manage or disable cookies through your browser settings. Disabling essential cookies may
                prevent the dashboard and login from functioning correctly.
              </Section>
              <Section title="4. Third-Party Cookies">
                Some features (e.g. Discord authentication, Premium payment processing) may set third-party cookies
                subject to their respective privacy policies.
              </Section>
              <Section title="5. Changes to This Policy">
                We may update this Cookie Policy periodically. Please check it regularly.
              </Section>
              <Section title="6. Contact">
                For questions about this Cookie Policy, contact us via the{" "}
                <a href="/support/contact" className="text-primary hover:underline">
                  Contact Us
                </a>{" "}
                page.
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-2">
    <h2 className="text-lg font-semibold text-foreground">{title}</h2>
    <p>{children}</p>
  </div>
);

export default CookiePolicy;
